import { Link, useNavigate } from 'react-router-dom';
import { CaretRight, Minus, PencilSimple, Trash, TrendDown, TrendUp } from '@phosphor-icons/react';

import { paths } from '../../routes/paths';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';
import { formatEUR } from '../../utils/money';
import { monthShortLabel, resolvePortraitDelta } from '../../lib/selfieDisplayUtils';

// .pen: E2U1fZ/OxWHv/pmPrf (ds/badge/delta--up/--down/--neutral)
function DeltaBadge({ snapshot }) {
  const delta = resolvePortraitDelta(snapshot);

  if (delta.kind === 'baseline' || delta.kind === 'flat') {
    return (
      <span
        data-ds-id="ds/badge/delta--neutral"
        className="inline-flex w-fit items-center gap-space-1 rounded-ds-sm bg-muted px-space-2 py-space-1 text-type-chip font-bold text-muted-foreground"
      >
        <Minus className="size-3" weight="bold" aria-hidden />
        {delta.kind === 'baseline' ? pt.snapshots.history.portrait.baseline : formatEUR(0)}
      </span>
    );
  }

  const isUp = delta.kind === 'up';
  const Icon = isUp ? TrendUp : TrendDown;
  return (
    <span
      data-ds-id={isUp ? 'ds/badge/delta--up' : 'ds/badge/delta--down'}
      className={cn(
        'inline-flex w-fit items-center gap-space-1 rounded-ds-sm px-space-2 py-space-1 text-type-chip font-bold',
        isUp ? 'bg-sage-tint text-sage-on-tint' : 'bg-warning-tint text-warning-fg',
      )}
    >
      <Icon className="size-3" weight="bold" aria-hidden />
      {isUp ? '+' : ''}
      {formatEUR(delta.value)}
    </span>
  );
}

// .pen: vZXhO (ds/table/row-actions) — reutilizado como botão redondo 28px genérico
function RowActionButton({ icon: Icon, tone = 'default', ...props }) {
  return (
    <button
      type="button"
      className={cn(
        'flex size-7 shrink-0 items-center justify-center rounded-ds-full border border-border bg-card',
        tone === 'danger' ? 'text-danger-fg' : 'text-muted-foreground',
      )}
      {...props}
    >
      <Icon className="size-3.5" weight="regular" aria-hidden />
    </button>
  );
}

/** .pen: EVHAy (ds/selfie/portrait-row) */
export function SelfiePortraitList({ snapshots = [], onDelete }) {
  const navigate = useNavigate();

  return (
    <ul className="flex flex-col gap-space-3">
      {snapshots.map((snapshot) => (
        <li key={snapshot.id}>
          <div
            data-ds-id="ds/selfie/portrait-row"
            className="flex items-center gap-space-3 rounded-ds-md border border-border bg-card px-space-3 py-space-2-5"
          >
            <Link
              to={paths.selfieReport(snapshot.id)}
              className="flex min-w-0 flex-1 items-center gap-space-3"
            >
              <div className="flex w-11 shrink-0 flex-col items-center gap-1">
                <p className="font-display text-type-label-lg font-bold leading-[1.2] text-sage-on-tint">
                  {monthShortLabel(snapshot.reference_month)}
                </p>
                <p className="text-type-chip text-muted-foreground">{snapshot.reference_year}</p>
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-space-1">
                <span className="font-display text-type-display-row font-bold tracking-[-0.15px] text-card-foreground tabular-nums">
                  {formatEUR(Number(snapshot.net_worth_eur ?? 0))}
                </span>
                <DeltaBadge snapshot={snapshot} />
              </div>
              <CaretRight className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            </Link>
            <div className="flex shrink-0 items-center gap-space-1">
              <RowActionButton
                icon={PencilSimple}
                onClick={() => navigate(paths.selfieEdit(snapshot.id))}
                aria-label={pt.snapshots.history.editLabel}
              />
              <RowActionButton
                icon={Trash}
                tone="danger"
                onClick={() => onDelete?.(snapshot)}
                aria-label={pt.snapshots.history.deleteLabel}
              />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export default SelfiePortraitList;
